/*
 * @Author: HB-BruceWang
 * @Date: 2025-10-20 17:22:37
 * @LastEditors: HB-BruceWang
 * @LastEditTime: 2025-10-22 14:55:28
 * @FilePath: \shopify-web\src\common\plugins\insider-register-integration\check.js
 * @Description: Insider插件 与 免密注册集成
 *
 * Copyright (c) 2025 by Hello Tech, All Rights Reserved.
 */

import {getUserExistStatus,getRegisterCoupon } from "@utils/api.js";


// URL参数处理工具函数
function updateURLParam(key, value = null) {
    const url = new URL(window.location.href);
    if (value === null) {
        url.searchParams.delete(key);
        window.history.replaceState(null, '', url);
    } else {
        url.searchParams.set(key, value);
    }
    return url.toString();
}

/**
 * 通用轮询函数
 * 持续调用异步回调直到回调返回真值（truthy），或达到超时、达到最大尝试次数，或页面卸载/不可见时停止。
 *
 * 行为说明：
 * - 每隔 interval 毫秒调用一次 callback（await 后再等待下一次）。
 * - 提前停止条件：
 *   - callback 返回真值（此值作为 poll 的结果返回）；
 *   - 已用时长 >= timeoutMs；
 *   - attempts 超过 maxAttempts；
 *   - 页面开始卸载（beforeunload）或变为不可见（visibilitychange）；
 * - 注意：attempts 在每次尝试前自增一次。若 attempts 超出 maxAttempts，循环终止且不再调用 callback。
 * - 如果 callback 抛出错误或返回被拒绝的 Promise，该错误会向外抛出（并且 finally 中会移除 beforeunload 监听器）。
 *
 * @template T
 * @param {() => (T|Promise<T>)} callback - 要反复调用的异步函数；当返回真值时，poll 解析为该值。
 * @param {Object} [options] - 可选配置对象。
 * @param {number} [options.interval=1000] - 每次尝试之间的等待时间（毫秒）。
 * @param {number} [options.maxAttempts=20] - 最大尝试次数（超过后停止）。
 * @param {number} [options.timeoutMs=20000] - 最长轮询总时长（毫秒）。
 * @returns {Promise<T|null>} - 如果 callback 在限定条件内返回真值则解析为该值；否则在超时/达到最大尝试/页面卸载或显式停止时返回 null。
 *
 * @throws 将传播 callback 抛出的任何错误或被拒绝的 Promise。
 *
 * 示例：
 *  每 200ms 检查一次元素，最多等待 5s：
 *  const el = await poll(() => document.querySelector('#my'), { interval: 200, timeoutMs: 5000 });
 */
async function poll(callback, {
    interval = 1000,
    maxAttempts = 20,
    timeoutMs = 40000
} = {}) {
    let attempts = 0;
    let start = 0; // 首次调用 callback 时再赋值
    let stopped = false;

    const stop = () => {
        stopped = true;
        // 清除URL参数
        updateURLParam('insider', null);
    };

    const visibilityHandler = () => { if (document.hidden) stop(); };

    window.addEventListener('beforeunload', stop, { once: true });
    document.addEventListener('visibilitychange', visibilityHandler);

    try {
        while (!stopped) {
            if (++attempts > maxAttempts) break;
            // 第一次尝试时记录开始时间
            if (attempts === 1) start = Date.now();
            if ((Date.now() - start) >= timeoutMs) break;

            const result = await callback();
            // 如果返回数据中 couponList 长度不为 0，则返回该结果；否则继续轮询
            if (result?.couponList?.length) {
                return result;
            }
            await new Promise(resolve => setTimeout(resolve, interval));
        }
    } finally {
        window.removeEventListener('beforeunload', stop);
        document.removeEventListener('visibilitychange', visibilityHandler);
    }
    return null;
}

// 获取注册优惠券信息（因为接口是异步生成的且生成对应券福利需要时间，所以需要轮询）
async function getCouponInfo() {
    try {
        const info = await getRegisterCoupon();
        if (!info?.success) {
            console.warn('Register coupon not ready');
            return null;
        }
        return info.data;
    } catch (err) {
        console.error('Failed to get register coupon:', err);
        return null;
    }
}

// 显示Insider插件对应的免密注册弹窗
function showInsiderRegisterPop() {
    const isLoggedIn = window?.pageInfo?.customerInfo?.id != '';
    if (isLoggedIn) return;

    const hasInsider = new URLSearchParams(window.location.search).has('insider');
    if (hasInsider) return;

    const returnUrl = updateURLParam('insider', 'true');

    window.jackeryLoginApp?.modalLogin({
        return_url: returnUrl,
        register_act: 'Insider-Register',
    });

    // 监听登录按钮点击事件，校验当前邮箱是否已注册
    (function () {
        const containerSelector = '.jackery-login__main';
        const buttonSelector = 'jackery-button';
        const emailSelector = '#jackery-field-0';

        let observer = null;
        let attached = false;
        let inFlight = false;
        let debounceTimer = null;

        async function checkEmailAndMark(btn) {
            // 防重入
            if (inFlight) return;

            const container = btn.closest(containerSelector) || document.querySelector(containerSelector) || document.body;
            const email = container.querySelector(emailSelector)?.value?.trim();
            if (!email) return;

            inFlight = true;
            try {
                const res = await getUserExistStatus({ email });
                if (!res?.data) {
                    localStorage.setItem('isNewUser', 'true');
                }
            } catch (err) {
                console.error('getUserExistStatus failed:', err);
            } finally {
                inFlight = false;
            }
        }

        function onLoginClick(e,btn) {
            // 防止频繁触发网络请求：短 debounce
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => checkEmailAndMark(btn), 120);
        }

        function tryAttach() {
            if (attached) return;
            const container = document.querySelector(containerSelector) || document.body;
            const btn = container.querySelector(buttonSelector);
            if (!btn) return;

            // 防止重复绑定：以 dataset 标记
            if (btn.dataset.insiderListenerAttached) {
                attached = true;
                return;
            }
            btn.addEventListener('click', (e) => onLoginClick(e, btn));
            btn.dataset.insiderListenerAttached = '1';
            attached = true;
            if (observer) {
                observer.disconnect();
                observer = null;
            }
        }

        // 优先尝试直接绑定，失败则监听 container 的子树变化（debounced）
        tryAttach();

        // 若未找到按钮，使用 MutationObserver 监听 DOM 变化（低开销）
        if (!attached) {
            const containerEl = document.querySelector(containerSelector) || document.body;
            let mutationDebounce = null;
            observer = new MutationObserver(() => {
                if (mutationDebounce) return;
                mutationDebounce = setTimeout(() => {
                    mutationDebounce = null;
                    tryAttach();
                }, 100);
            });
            observer.observe(containerEl, { childList: true, subtree: true, attributes: false });
            // 安全超时断开 observer
            const observerTimeout = setTimeout(() => {
                if (observer) {
                    observer.disconnect();
                    observer = null;
                }
            }, 30000);

            // 页面卸载时清理 observer & timeout & debounce
            window.addEventListener('beforeunload', () => {
                if (observer) observer.disconnect();
                clearTimeout(observerTimeout);
                if (mutationDebounce) clearTimeout(mutationDebounce);
            }, { once: true });
        } else {
            // 已绑定时，确保在卸载时移除 debounce timer
            window.addEventListener('beforeunload', () => {
                if (debounceTimer) clearTimeout(debounceTimer);
            }, { once: true });
        }
    })();
}

// 仅在必须时以不可枚举方式暴露，减少全局污染（保留原使用方式兼容）
if (typeof window !== 'undefined' && !Object.prototype.hasOwnProperty.call(window, 'showInsiderRegisterPop')) {
    Object.defineProperty(window, 'showInsiderRegisterPop', {
        value: showInsiderRegisterPop,
        writable: false,
        configurable: true,
        enumerable: false
    });
}


// 主逻辑
document.addEventListener('DOMContentLoaded', async () => {
    const hasInsider = new URLSearchParams(window.location.search).has('insider');
    if (!hasInsider) return;

    const isLoggedIn = window?.pageInfo?.customerInfo?.id != '';
    if(isLoggedIn){
        const isNewUser = localStorage.getItem('isNewUser') === 'true';
        if(!isNewUser){
            // 登录的是老用户，清除URL参数
            updateURLParam('insider', null);
            return;
        }

        // 登录的是新用户，展示注册福利
        const couponData = await poll(getCouponInfo);
        if (couponData) {
            // 显示优惠券信息
            document.querySelector('insider-register-integration-welfare-pop')?.initShow(couponData);
            // 清除URL参数
            updateURLParam('insider', null);
            localStorage.removeItem('isNewUser');
        }
    }
});